import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Package, Thermometer, Calendar, DollarSign } from 'lucide-react';

const CustomerWarehouseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [mhe, setMhe] = useState([]);
  const [manpower, setManpower] = useState([]);
  const [basket, setBasket] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchWarehouseDetails();
  }, [id]);

  const fetchWarehouseDetails = async () => {
    try {
      const [warehouseRes, mheRes, manpowerRes] = await Promise.all([
        axios.get(`${API_URL}/warehouses/${id}`),
        axios.get(`${API_URL}/warehouses/${id}/mhe`),
        axios.get(`${API_URL}/warehouses/${id}/manpower`)
      ]);
      setWarehouse(warehouseRes.data);
      setMhe(mheRes.data);
      setManpower(manpowerRes.data);
    } catch (error) {
      toast.error('Failed to load warehouse details');
      navigate('/customer/search');
    } finally {
      setLoading(false);
    }
  };

  const addToBasket = (item, itemType) => {
    setBasket([...basket, { ...item, item_type: itemType, quantity: 1 }]);
    toast.success('Added to basket');
  };

  const removeFromBasket = (index) => {
    const newBasket = [...basket];
    newBasket.splice(index, 1);
    setBasket(newBasket);
  };

  const calculateTotal = () => {
    return basket.reduce((total, item) => {
      const price = item.item_type === 'space' ? item.price : 
                    item.hourly_rate ? item.hourly_rate * item.quantity : 
                    item.shift_rate ? item.shift_rate * item.quantity : 0;
      return total + price;
    }, 0);
  };

  const handleBooking = async () => {
    if (basket.length === 0) {
      toast.error('Please add items to basket');
      return;
    }

    const bookingItems = basket.map(item => ({
      item_type: item.item_type,
      item_id: item.id,
      quantity: item.quantity,
      unit_type: item.unit_type || 'sqm',
      duration: 1,
      price: item.item_type === 'space' ? item.price : 
             item.hourly_rate ? item.hourly_rate : item.shift_rate
    }));

    try {
      await axios.post(`${API_URL}/bookings`, {
        warehouse_id: id,
        items: bookingItems,
        start_date: new Date().toISOString()
      });
      toast.success('Booking created successfully');
      navigate('/customer/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="font-heading text-3xl font-black text-foreground tracking-tight mb-2">
                    {warehouse.name}
                  </h1>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                    <span>{warehouse.location.address}, {warehouse.location.city}, {warehouse.location.state}</span>
                  </div>
                </div>
                {warehouse.temperature_controlled && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-100 border border-blue-200 rounded-sm">
                    <Thermometer className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                    <span className="text-sm font-semibold text-blue-700">Temp Controlled</span>
                  </div>
                )}
              </div>

              <p className="text-muted-foreground mb-6">{warehouse.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {warehouse.bulk_floor_sqm > 0 && (
                  <div className="p-4 bg-secondary rounded-sm">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Bulk Floor</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{warehouse.bulk_floor_sqm} SQM</p>
                  </div>
                )}
                {warehouse.racking_positions > 0 && (
                  <div className="p-4 bg-secondary rounded-sm">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Racking</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{warehouse.racking_positions} PP</p>
                  </div>
                )}
                {warehouse.variable_cbm > 0 && (
                  <div className="p-4 bg-secondary rounded-sm">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Variable</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{warehouse.variable_cbm} CBM</p>
                  </div>
                )}
              </div>
            </Card>

            <Tabs defaultValue="space" className="w-full">
              <TabsList className="w-full grid grid-cols-3 bg-secondary rounded-sm p-1">
                <TabsTrigger value="space" data-testid="space-tab" className="rounded-sm">Storage Space</TabsTrigger>
                <TabsTrigger value="mhe" data-testid="mhe-tab" className="rounded-sm">MHE</TabsTrigger>
                <TabsTrigger value="manpower" data-testid="manpower-tab" className="rounded-sm">Manpower</TabsTrigger>
              </TabsList>

              <TabsContent value="space" className="mt-4">
                <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-4">Available Space</h3>
                  <div className="space-y-4">
                    {warehouse.price_per_sqm > 0 && (
                      <div className="flex items-center justify-between p-4 border border-border rounded-sm">
                        <div>
                          <p className="font-medium text-foreground">Bulk Floor Space</p>
                          <p className="text-sm text-muted-foreground">Price per SQM</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-foreground">${warehouse.price_per_sqm}/SQM</p>
                          <Button
                            onClick={() => addToBasket({ id: 'space-sqm', price: warehouse.price_per_sqm, unit_type: 'sqm' }, 'space')}
                            size="sm"
                            data-testid="add-space-sqm"
                            className="mt-2 rounded-sm"
                          >
                            Add to Basket
                          </Button>
                        </div>
                      </div>
                    )}
                    {warehouse.price_per_pp > 0 && (
                      <div className="flex items-center justify-between p-4 border border-border rounded-sm">
                        <div>
                          <p className="font-medium text-foreground">Racking Position</p>
                          <p className="text-sm text-muted-foreground">Price per Pallet Position</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-foreground">${warehouse.price_per_pp}/PP</p>
                          <Button
                            onClick={() => addToBasket({ id: 'space-pp', price: warehouse.price_per_pp, unit_type: 'pp' }, 'space')}
                            size="sm"
                            data-testid="add-space-pp"
                            className="mt-2 rounded-sm"
                          >
                            Add to Basket
                          </Button>
                        </div>
                      </div>
                    )}
                    {warehouse.price_per_cbm > 0 && (
                      <div className="flex items-center justify-between p-4 border border-border rounded-sm">
                        <div>
                          <p className="font-medium text-foreground">Variable Storage</p>
                          <p className="text-sm text-muted-foreground">Price per CBM</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-foreground">${warehouse.price_per_cbm}/CBM</p>
                          <Button
                            onClick={() => addToBasket({ id: 'space-cbm', price: warehouse.price_per_cbm, unit_type: 'cbm' }, 'space')}
                            size="sm"
                            data-testid="add-space-cbm"
                            className="mt-2 rounded-sm"
                          >
                            Add to Basket
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="mhe" className="mt-4">
                <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-4">Material Handling Equipment</h3>
                  {mhe.length === 0 ? (
                    <p className="text-muted-foreground">No MHE available at this warehouse</p>
                  ) : (
                    <div className="space-y-4">
                      {mhe.map((equipment) => (
                        <div key={equipment.id} className="flex items-center justify-between p-4 border border-border rounded-sm">
                          <div>
                            <p className="font-medium text-foreground">{equipment.equipment_name}</p>
                            <p className="text-sm text-muted-foreground">{equipment.equipment_type}</p>
                            <p className="text-xs text-muted-foreground mt-1">Qty: {equipment.quantity_available}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-foreground">
                              ${equipment.hourly_rate}/hr or ${equipment.shift_rate}/shift
                            </p>
                            <Button
                              onClick={() => addToBasket(equipment, 'mhe')}
                              size="sm"
                              data-testid={`add-mhe-${equipment.id}`}
                              className="mt-2 rounded-sm"
                            >
                              Add to Basket
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="manpower" className="mt-4">
                <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-4">Manpower Services</h3>
                  {manpower.length === 0 ? (
                    <p className="text-muted-foreground">No manpower services available</p>
                  ) : (
                    <div className="space-y-4">
                      {manpower.map((service) => (
                        <div key={service.id} className="flex items-center justify-between p-4 border border-border rounded-sm">
                          <div>
                            <p className="font-medium text-foreground">{service.service_name}</p>
                            <p className="text-sm text-muted-foreground">{service.service_type}</p>
                            <p className="text-xs text-muted-foreground mt-1">Qty: {service.quantity_available}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-foreground">
                              ${service.hourly_rate}/hr or ${service.shift_rate}/shift
                            </p>
                            <Button
                              onClick={() => addToBasket(service, 'manpower')}
                              size="sm"
                              data-testid={`add-manpower-${service.id}`}
                              className="mt-2 rounded-sm"
                            >
                              Add to Basket
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="lg:col-span-1">
            <Card className="bg-white border border-border rounded-sm shadow-sm p-6 sticky top-4">
              <h3 className="font-heading text-xl font-bold text-foreground mb-4">Storage Basket</h3>
              
              {basket.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Your basket is empty</p>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {basket.map((item, index) => (
                      <div key={index} className="p-3 bg-secondary rounded-sm">
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-medium text-foreground">
                            {item.item_type === 'space' ? `Storage Space (${item.unit_type.toUpperCase()})` : 
                             item.equipment_name || item.service_name}
                          </p>
                          <button
                            onClick={() => removeFromBasket(index)}
                            className="text-destructive text-xs font-medium hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-bold text-foreground">${calculateTotal().toFixed(2)}</span>
                    </div>
                    {warehouse.gate_charges_inbound > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Gate Charges</span>
                        <span className="text-foreground">${warehouse.gate_charges_inbound}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleBooking}
                    data-testid="book-now-button"
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm transition-all active:scale-95"
                  >
                    Book Now
                  </Button>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerWarehouseDetail;